import { cleanCustomerId } from '../../Services/app/operatorservice';
// 2026-06-25: Live Worker Board — supervisor view of who's clocked in where
// Phase 1 of Epicor-like MES/shopfloor system
import React, { useCallback, useEffect, useState } from 'react';
import { getLiveBoard, getClockHistory, getTodaySummary } from '../../Services/app/clockservice';

const BRAND = {
  orange: '#f97316',
  green: '#16a34a',
  red: '#dc2626',
  blue: '#2563eb',
  textDark: '#111827',
  textMuted: '#6b7280',
  border: '#e5e7eb',
  bg: '#f9fafb',
};

function fmtMin(min) {
  if (min == null) return '–';
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function fmtTime(iso) {
  if (!iso) return '–';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  } catch { return '–'; }
}

function ActivityPill({ activity }) {
  const colors = {
    RUN: { bg: '#dcfce7', border: BRAND.green, text: '#14532d' },
    SETUP: { bg: '#dbeafe', border: BRAND.blue, text: '#1e3a8a' },
    INSPECT: { bg: '#fef3c7', border: '#d97706', text: '#7a4a00' },
    BREAK: { bg: '#fee2e2', border: BRAND.red, text: '#7a0000' },
    IDLE: { bg: '#f3f4f6', border: BRAND.textMuted, text: '#374151' },
    GENERAL: { bg: '#f3f4f6', border: BRAND.textMuted, text: '#374151' },
  };
  const c = colors[activity] || colors.GENERAL;
  return (
    <span style={{ padding: '2px 8px', background: c.bg, border: `1px solid ${c.border}`,
                   borderRadius: 4, fontSize: 11, fontWeight: 700, color: c.text }}>
      {activity || 'GENERAL'}
    </span>
  );
}

export default function LabourBoard() {
  const customerId = cleanCustomerId(localStorage.getItem('CustomerID') || window._env_.CUSTOMER_ID);
  const [live, setLive] = useState({ total_clocked_in: 0, machines_covered: 0, entries: [] });
  const [today, setToday] = useState({});
  const [history, setHistory] = useState({ rows: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const refresh = useCallback(async () => {
    if (!customerId) {
      setErr('No customer_id available');
      setLoading(false);
      return;
    }
    try {
      const [l, t, h] = await Promise.all([
        getLiveBoard(customerId),
        getTodaySummary(customerId),
        getClockHistory({ customer_id: customerId, limit: 50 }),
      ]);
      setLive(l);
      setToday(t);
      setHistory(h);
      setLastUpdated(new Date());
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 15000);
    return () => clearInterval(id);
  }, [refresh]);

  const kpi = [
    { label: 'Currently Clocked In', value: live.total_clocked_in, color: BRAND.green, sub: `${live.machines_covered} machines covered` },
    { label: 'Operators Today', value: today.operators_today || 0, color: BRAND.blue, sub: 'unique badges' },
    { label: 'Entries Today', value: today.entries_today || 0, color: BRAND.orange, sub: 'in + out cycles' },
    { label: 'Worker Hours Today', value: fmtMin(parseInt(today.labour_minutes_today || 0, 10)), color: BRAND.textDark, sub: 'closed entries only' },
  ];

  return (
    <div style={{ padding: 20, background: BRAND.bg, minHeight: '100vh', fontFamily: 'inherit' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, color: BRAND.textDark }}>Live Worker Board</div>
          <div style={{ fontSize: 12, color: BRAND.textMuted }}>
            Who's clocked in where, right now {lastUpdated && `· last updated ${fmtTime(lastUpdated.toISOString())}`}
          </div>
        </div>
        <button onClick={refresh} style={{ padding: '8px 16px', background: BRAND.orange, color: '#fff',
                                            border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 700,
                                            cursor: 'pointer' }}>↻ Refresh</button>
      </div>

      {err && (
        <div style={{ padding: 12, background: '#fee2e2', border: `1px solid ${BRAND.red}`, borderRadius: 6,
                      color: '#7a0000', fontSize: 13, marginBottom: 12 }}>{err}</div>
      )}

      {/* KPI tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
        {kpi.map(k => (
          <div key={k.label} style={{ background: '#fff', border: `1px solid ${BRAND.border}`,
                                      borderRadius: 8, padding: 14 }}>
            <div style={{ fontSize: 11, color: BRAND.textMuted, fontWeight: 700, textTransform: 'uppercase' }}>{k.label}</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: k.color, marginTop: 4 }}>{k.value}</div>
            <div style={{ fontSize: 11, color: BRAND.textMuted, marginTop: 2 }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Live entries */}
      <div style={{ background: '#fff', border: `1px solid ${BRAND.border}`, borderRadius: 8, marginBottom: 16 }}>
        <div style={{ padding: '12px 14px', borderBottom: `1px solid ${BRAND.border}`,
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 14, fontWeight: 800 }}>Currently Clocked In</div>
          <div style={{ fontSize: 12, color: BRAND.textMuted }}>{live.entries.length} active</div>
        </div>
        {loading && <div style={{ padding: 24, textAlign: 'center', color: BRAND.textMuted }}>Loading…</div>}
        {!loading && live.entries.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: BRAND.textMuted, fontSize: 13 }}>
            Nobody clocked in right now.
          </div>
        )}
        {!loading && live.entries.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: BRAND.bg, borderBottom: `1px solid ${BRAND.border}` }}>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: BRAND.textMuted, textTransform: 'uppercase' }}>Operator</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: BRAND.textMuted, textTransform: 'uppercase' }}>Machine</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: BRAND.textMuted, textTransform: 'uppercase' }}>Activity</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: BRAND.textMuted, textTransform: 'uppercase' }}>WO / Op</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: BRAND.textMuted, textTransform: 'uppercase' }}>Since</th>
                <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: 11, fontWeight: 700, color: BRAND.textMuted, textTransform: 'uppercase' }}>Elapsed</th>
              </tr>
            </thead>
            <tbody>
              {live.entries.map(e => (
                <tr key={e.id} style={{ borderBottom: `1px solid ${BRAND.border}` }}>
                  <td style={{ padding: '10px 14px', fontWeight: 700, color: BRAND.textDark }}>{e.operator_name}</td>
                  <td style={{ padding: '10px 14px' }}>{e.machine_name || <span style={{ color: BRAND.textMuted }}>–</span>}</td>
                  <td style={{ padding: '10px 14px' }}><ActivityPill activity={e.activity} /></td>
                  <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 12 }}>
                    {e.wo_number || <span style={{ color: BRAND.textMuted }}>–</span>}
                    {e.op_seq && <span style={{ color: BRAND.textMuted }}> / {e.op_seq}</span>}
                  </td>
                  <td style={{ padding: '10px 14px', color: BRAND.textMuted }}>{fmtTime(e.clock_in_ts)}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, color: BRAND.green }}>{fmtMin(e.elapsed_min)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Recent history */}
      <div style={{ background: '#fff', border: `1px solid ${BRAND.border}`, borderRadius: 8 }}>
        <div style={{ padding: '12px 14px', borderBottom: `1px solid ${BRAND.border}` }}>
          <div style={{ fontSize: 14, fontWeight: 800 }}>Recent Clock-Outs (last 7 days, max 50)</div>
        </div>
        {history.rows.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: BRAND.textMuted, fontSize: 13 }}>No history yet.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: BRAND.bg, borderBottom: `1px solid ${BRAND.border}` }}>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: BRAND.textMuted, textTransform: 'uppercase' }}>Operator</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: BRAND.textMuted, textTransform: 'uppercase' }}>Machine</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: BRAND.textMuted, textTransform: 'uppercase' }}>Activity</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: BRAND.textMuted, textTransform: 'uppercase' }}>In</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: BRAND.textMuted, textTransform: 'uppercase' }}>Out</th>
                <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: 11, fontWeight: 700, color: BRAND.textMuted, textTransform: 'uppercase' }}>Duration</th>
              </tr>
            </thead>
            <tbody>
              {history.rows.map(r => (
                <tr key={r.id} style={{ borderBottom: `1px solid ${BRAND.border}` }}>
                  <td style={{ padding: '10px 14px', fontWeight: 700 }}>{r.operator_name}</td>
                  <td style={{ padding: '10px 14px' }}>{r.machine_name || <span style={{ color: BRAND.textMuted }}>–</span>}</td>
                  <td style={{ padding: '10px 14px' }}><ActivityPill activity={r.activity} /></td>
                  <td style={{ padding: '10px 14px', color: BRAND.textMuted }}>{fmtTime(r.clock_in_ts)}</td>
                  <td style={{ padding: '10px 14px', color: BRAND.textMuted }}>{r.clock_out_ts ? fmtTime(r.clock_out_ts) : <span style={{ color: BRAND.orange, fontWeight: 700 }}>OPEN</span>}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700 }}>{fmtMin(r.duration_min)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
